import React, { useContext } from 'react';

import { Form, FormGroup, InputGroup, Well } from 'react-bootstrap';
import { Controller, useForm } from 'react-hook-form';
import { AxiosError } from 'axios';
import Select, { ValueType } from 'react-select';

import * as OrdersService from '../../../services/OrdersService';
import { ActivityContext } from '../../../contexts/Activity';
import { convertNumberToUSD } from '../../../infrastructure/utils/common';
import { Reason } from '../../../types/Order';
import InfoNote from '../../utils/InfoNote';

type Props = {
  orderId: string;
  refund: {
    limit: number;
  };
  reverse: {
    limit: number;
    allowed: boolean;
  };
  onBack(): void;
  showBackButton: boolean;
  onSuccess(): void;
};

type FormData = {
  reason?: ValueType<{ value: Reason; label: string }, false>;
  refund?: number;
  reverse?: number;
  notes?: string;
};
type ValidFormData = {
  reason?: ValueType<{ value: Reason; label: string }, false>;
  refund: number;
  reverse?: number;
  notes: string;
};

const reasonOptions = [
  { value: 'requested_by_customer', label: 'Requested by customer' },
  { value: 'duplicate', label: 'Duplicate' },
  { value: 'fraudulent', label: 'Fraudulent' },
  { value: 'other', label: 'Other' },
];

export default function RequestRefundPage(props: Props) {
  const activityContext = useContext(ActivityContext);

  const {
    formState: { errors, isValid, isSubmitting, isSubmitSuccessful },
    clearErrors,
    control,
    handleSubmit,
    register,
    setError,
    trigger,
  } = useForm<FormData, undefined, ValidFormData>({
    defaultValues: {},
    mode: 'onChange',
  });

  const handleRequestRefundClick = async (data: ValidFormData) => {
    clearErrors();
    try {
      await OrdersService.requestPartialRefund(props.orderId, {
        refundAmount: Math.round(data.refund * 100),
        reverseAmount: Math.round((data.reverse || 0) * 100),
        reason: data.reason?.value || 'other',
        reasonNote: data.notes,
      });

      props.onSuccess();
    } catch (e) {
      const error = e as AxiosError;
      if (error.isAxiosError) {
        setError('root.server', {
          type: (error.response?.status || 500).toString(),
          message: error.response?.data,
        });
      } else {
        setError('root.server', {
          type: '500',
          message: 'Error occured during partial refund submission',
        });
      }

      // hack to unblock 'Refund' button after server error
      // react-hook-form set 'isValid' to false when 'setError' called manually
      // but fields validation (since everything is valid), mark form state as valid again
      // 'trigger' is re-validate all fields
      trigger();
    }
  };

  const refundInput = register('refund', {
    valueAsNumber: true,
    required: {
      value: true,
      message: 'Refund amount is required',
    },
    max: {
      value: props.refund.limit / 100,
      message: 'The maximum refund amount must not exceed the total cost',
    },
    min: {
      value: 0,
      message: "Refund amount can't be negative",
    },
  });
  const reverseInput = register('reverse', {
    valueAsNumber: true,
    max: {
      value: props.reverse.limit / 100,
      message: 'The maximum amount to reverse must not exceed the total',
    },
    min: {
      value: 0,
      message: "Reverse amount can't be negative",
    },
  });

  return (
    <Form
      id='requestRefundModalForm'
      onSubmit={handleSubmit(handleRequestRefundClick)}
    >
      <header>
        <h4>Request a refund</h4>
      </header>
      <main>
        <div className='request-refund-modal__page-content'>
          {activityContext.cancellationPolicy && (
            <Well>
              <h4>Cancellation Policy</h4>
              {activityContext.cancellationPolicy}
            </Well>
          )}
          <section className='request-refund-modal__form'>
            <FormGroup>
              <p>Refund reason:</p>
              <Controller
                name='reason'
                control={control}
                render={({ field }) => (
                  <Select
                    {...field}
                    options={reasonOptions}
                    openMenuOnFocus={false}
                    styles={{ menu: base => ({ ...base, zIndex: 9999 }) }}
                  />
                )}
              />
            </FormGroup>
            <FormGroup>
              <p>
                Amount to refund user:{' '}
                <small className='text-muted'>
                  maximum {convertNumberToUSD(props.refund.limit / 100)}
                </small>
              </p>
              <InputGroup>
                <input
                  type='number'
                  className='form-control'
                  placeholder='0.00'
                  step={0.01}
                  {...refundInput}
                  onChange={e => {
                    refundInput.onChange(e);
                  }}
                />
                <InputGroup.Addon>$</InputGroup.Addon>
              </InputGroup>
              {errors.refund?.message && (
                <small className='text-danger'>{errors.refund?.message}</small>
              )}
            </FormGroup>

            {props.reverse.allowed && (
              <FormGroup>
                <p>
                  Amount to reverse from vendor:{' '}
                  <small className='text-muted'>
                    maximum {convertNumberToUSD(props.reverse.limit / 100)}
                  </small>
                </p>
                <InputGroup>
                  <input
                    type='number'
                    className='form-control'
                    placeholder={`0.00 (If left blank, no money will be reversed from the vendor's Stripe balance)`}
                    step={0.01}
                    {...reverseInput}
                    onChange={e => {
                      reverseInput.onChange(e);
                    }}
                  />
                  <InputGroup.Addon>$</InputGroup.Addon>
                </InputGroup>
                {errors.reverse?.message && (
                  <small className='text-danger'>
                    {errors.reverse?.message}
                  </small>
                )}
              </FormGroup>
            )}
            {props.reverse.allowed === false && (
              <InfoNote variant='info' faIcon='fa-info-circle'>
                <p>
                  Reversing funds from the vendor isn&apos;t supported for BACH
                  orders.
                </p>
                <p>Only the refund to the customer will be requested.</p>
              </InfoNote>
            )}

            <FormGroup>
              <p>Notes:</p>
              <textarea
                rows={3}
                className='form-control'
                {...register('notes', {
                  required: {
                    value: true,
                    message: 'Notes are required',
                  },
                })}
              />
              {errors.notes?.message && (
                <small className='text-danger'>{errors.notes?.message}</small>
              )}
            </FormGroup>
          </section>
        </div>
      </main>
      <footer className='modal__footer'>
        <menu
          className={`modal__actions ${
            props.showBackButton
              ? 'modal__actions--between'
              : 'modal__actions--end  '
          }`}
        >
          {props.showBackButton && (
            <button
              key='back'
              className='btn btn-secondary btn--wide'
              type='button'
              onClick={props.onBack}
            >
              Back
            </button>
          )}
          <button
            key='requestRefund'
            className='btn btn-primary btn--wide'
            type='submit'
            form='requestRefundModalForm'
            disabled={!isValid || isSubmitting || isSubmitSuccessful}
          >
            Request Refund
          </button>
        </menu>
      </footer>
    </Form>
  );
}
